import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import logo from '@Assets/images/logo-80.png';

/** Faint sheet-music staff lines + numbers behind the auth header. */
function AuthBackdrop() {
  const numbers = [
    { n: 5, left: '30%', top: 8 },
    { n: 3, left: '42%', top: 28 },
    { n: 4, left: '50%', top: 56 },
    { n: 2, left: '66%', top: 6 },
    { n: 4, left: '66%', top: 34 },
    { n: 3, left: '66%', top: 64 },
    { n: 1, left: '30%', top: 78 },
  ];
  return (
    <div
      aria-hidden="true"
      className={`
        pointer-events-none absolute inset-x-0 top-0 h-64 overflow-hidden
        opacity-40
        [mask-image:linear-gradient(to_bottom,black,transparent)]
      `}
    >
      <div className="mx-auto h-full max-w-[820px]">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="absolute inset-x-0 h-px bg-gray-300"
            style={{ top: 120 + i * 32 }}
          />
        ))}
        {numbers.map((item, i) => (
          <span
            key={i}
            className="absolute font-light text-gray-300"
            style={{ left: item.left, top: item.top, fontSize: 44 }}
          >
            {item.n}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Shared centered, card-less layout for the sign-in / sign-up screens. */
export function AuthLayout({
  title,
  subtitle,
  showLogo = true,
  children,
}: {
  title: string;
  subtitle?: string;
  showLogo?: boolean;
  children: ReactNode;
}) {
  return (
    <main
      className={`
        relative min-h-screen overflow-hidden bg-[#fbfcfc] font-[Outfit]
      `}
    >
      {showLogo && (
        <Link to="/" className="absolute top-9 left-9 z-10">
          <img src={logo} alt="PineappleMusicLab" className="h-12 w-12" />
        </Link>
      )}

      <AuthBackdrop />

      <div
        className={`
          relative z-10 mx-auto flex w-full max-w-[400px] flex-col px-4 pt-14
          pb-16
          sm:pt-24
          md:pt-34
          lg:pt-44
        `}
      >
        <h1 className="text-center text-2xl font-light text-[#0d0d0d]">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-2.5 text-center text-sm text-gray-500">{subtitle}</p>
        )}
        <div className="mt-10">{children}</div>
      </div>
    </main>
  );
}

export function AuthError({ message }: { message?: string | null }) {
  if (!message) return null;
  return (
    <p
      className={`
        mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm
        text-red-700
      `}
      role="alert"
    >
      {message}
    </p>
  );
}

export const authInputClass = `
  w-full rounded-lg border border-[rgba(75,76,76,0.18)] bg-white px-4 py-3
  text-gray-800 placeholder-gray-400
  focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40
`;

export const authButtonClass = `
  mt-2 min-h-[50px] w-full cursor-pointer rounded-full bg-[#333] px-5 py-3
  font-semibold text-white transition-transform duration-100
  hover:bg-black
  active:scale-95
  disabled:cursor-not-allowed disabled:opacity-50
`;
